import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
    { id: "home", label: "Home" },
    { id: "invitation", label: "Invitation" },
    { id: "timeline", label: "Timeline" },
    { id: "shukrana", label: "Shukrana" },
    { id: "nikah", label: "Nikah" },
    { id: "valima", label: "Valima" },
];

export const Navigation = () => {
    const [active, setActive] = useState("home");
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(e.target.id);
                });
            },
            { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
        );
        NAV_ITEMS.forEach((n) => {
            const el = document.getElementById(n.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const handleClick = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <>
            <motion.header
                initial={{ y: -60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.2, delay: 0.4, ease: "easeInOut" }}
                data-testid="site-navigation"
                className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-700 ease-in-out ${
                    scrolled
                        ? "bg-[#0f0d0a]/85 backdrop-blur-xl border-b border-[#2c251d]/80"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="mx-auto max-w-7xl px-6 md:px-12 py-5 flex items-center justify-between">
                    <button
                        onClick={() => handleClick("home")}
                        data-testid="nav-logo-button"
                        className="font-serif-display text-sm md:text-base tracking-[0.35em] uppercase text-[color:var(--charcoal)]"
                    >
                        R <span className="text-[color:var(--gold)]">&</span> Z
                    </button>

                    <nav className="hidden lg:flex items-center gap-8">
                        {NAV_ITEMS.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => handleClick(item.id)}
                                data-testid={`nav-link-${item.id}`}
                                className={`nav-link ${active === item.id ? "active" : ""}`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </nav>

                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden text-[color:var(--charcoal)] p-2"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        className="fixed inset-0 z-40 bg-[#0f0d0a]/98 backdrop-blur-2xl lg:hidden flex items-center justify-center"
                        data-testid="mobile-menu"
                    >
                        <nav className="flex flex-col items-center gap-8">
                            {NAV_ITEMS.map((item, i) => (
                                <motion.button
                                    key={item.id}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{
                                        delay: 0.05 * i,
                                        duration: 0.6,
                                        ease: "easeInOut",
                                    }}
                                    onClick={() => handleClick(item.id)}
                                    data-testid={`nav-mobile-link-${item.id}`}
                                    className="font-serif-display text-2xl tracking-[0.2em] uppercase text-[color:var(--charcoal)]"
                                >
                                    {item.label}
                                </motion.button>
                            ))}
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navigation;
